import { supabase } from '@/integrations/supabase/client';

const getAgentsCount = async (userId?: string): Promise<number> => {
  let query = supabase.from("agents").select("id", { count: "exact", head: true });

  if (userId) {
    query = query.eq("user_id", userId);
  }

  const { count, error } = await query;

  if (error) {
    if (error.code === "PGRST116" || count === null) {
      return 0;
    }
    console.error(`Error fetching agent count:`, error);
    throw error;
  }
  return count || 0;
};

const getConversationsCount = async (userId?: string): Promise<number> => {
  let query = supabase
    .from("conversations")
    .select("id, agents!inner(user_id)", { count: "exact", head: true })
    .or("is_marketplace_genie.is.null,is_marketplace_genie.eq.false", { foreignTable: "agents" });

  if (userId) {
    query = query.eq("agents.user_id", userId);
  }

  const { count, error } = await query;

  if (error) {
    if (error.code === "PGRST116" || count === null) {
      return 0;
    }
    console.error(`Error fetching conversation count:`, error);
    throw error;
  }
  return count || 0;
};

const getDocumentsCount = async (userId?: string): Promise<number> => {
  let query = supabase.from("documents").select("id", { count: "exact", head: true });

  if (userId) {
    query = query.eq("user_id", userId);
  }

  const { count, error } = await query;

  if (error) {
    if (error.code === "PGRST116" || count === null) {
      return 0;
    }
    console.error(`Error fetching document count:`, error);
    throw error;
  }
  return count || 0;
};

const getLast24hConversationsCount = async (agentIds: string[]): Promise<number> => {
  if (agentIds.length === 0) return 0;

  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const query = supabase
    .from("conversations")
    .select("id", { count: "exact", head: true })
    .gte("created_at", since)
    .in("agent_id", agentIds);

  const { count, error } = await query;
  if (error) {
    console.error("Failed to fetch last24h", error);
    return 0;
  }
  return count || 0;
};

const getConversationsTimeline30Days = async (agentIds: string[]): Promise<{ dates: string[]; counts: number[]; total: number }> => {
  if (agentIds.length === 0) return { dates: [], counts: [], total: 0 };

  const start = new Date();
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  const startISO = start.toISOString();

  const query = supabase.from("conversations").select("created_at").gte("created_at", startISO).in("agent_id", agentIds);

  const { data, error } = await query;
  if (error) {
    console.error("Failed to fetch timeline", error);
    return { dates: [], counts: [], total: 0 };
  }

  const days: string[] = [];
  const countsMap: Record<string, number> = {};
  const tmp = new Date(start);
  for (let i = 0; i < 30; i++) {
    const key = tmp.toISOString().slice(0, 10);
    days.push(key);
    countsMap[key] = 0;
    tmp.setDate(tmp.getDate() + 1);
  }

  (data || []).forEach((row: any) => {
    const key = new Date(row.created_at).toISOString().slice(0, 10);
    if (countsMap[key] !== undefined) countsMap[key] += 1;
  });

  const counts = days.map((d) => countsMap[d] || 0);
  const total = counts.reduce((a, b) => a + b, 0);
  return { dates: days, counts, total };
};

const getRecentConversationCountsByAgent = async (
  agentIds: string[],
  daysAgo: number = 7,
): Promise<Record<string, number>> => {
  if (agentIds.length === 0) return {};

  const since = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from("conversations")
    .select("agent_id")
    .in("agent_id", agentIds)
    .gte("created_at", since);

  if (error) {
    console.error("Failed to fetch recent conversation counts", error);
    return {};
  }

  const counts: Record<string, number> = {};
  (data || []).forEach((row: { agent_id: string | null }) => {
    if (row.agent_id) counts[row.agent_id] = (counts[row.agent_id] || 0) + 1;
  });
  return counts;
};

export const StatsService = {
  getAgentsCount,
  getConversationsCount,
  getDocumentsCount,
  getLast24hConversationsCount,
  getConversationsTimeline30Days,
  getRecentConversationCountsByAgent,
};
